/**
 * SourceDocUpload Component
 * Inline file upload for AE form section 5.0 (Source Documentation Submitted to OPCI)
 * Shows upload button per repeat, lists uploaded docs, allows download/delete
 */

import React, { useState, useEffect, useRef } from 'react';
import { Upload, Download, Trash2, FileText, Loader2 } from 'lucide-react';
import { sourceDocsApi, SourceDoc } from '../../services/api';

interface SourceDocUploadProps {
  subjectId: string;
  dataEntryId?: string;
  documentType: string;
  documentTypeLabel: string;
  disabled?: boolean;
}

export const SourceDocUpload: React.FC<SourceDocUploadProps> = ({
  subjectId,
  dataEntryId,
  documentType,
  documentTypeLabel,
  disabled = false,
}) => {
  const [docs, setDocs] = useState<SourceDoc[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load existing docs for this subject + document type
  useEffect(() => {
    if (!subjectId || !documentType) return;
    loadDocs();
  }, [subjectId, documentType]);

  const loadDocs = async () => {
    try {
      const filters: Record<string, string> = { subjectId };
      if (documentType) filters.documentType = documentType;
      const result = await sourceDocsApi.list(filters);
      setDocs(result.docs || []);
    } catch (err) {
      console.error('Failed to load source docs:', err);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('subjectId', subjectId);
      formData.append('documentType', documentType);
      formData.append('documentTypeLabel', documentTypeLabel);
      if (dataEntryId) formData.append('dataEntryId', dataEntryId);

      await sourceDocsApi.upload(formData);
      await loadDocs();
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (doc: SourceDoc) => {
    try {
      const result = await sourceDocsApi.getDownloadUrl(doc.docId);
      window.open(result.url, '_blank');
    } catch (err: any) {
      setError(err.message || 'Download failed');
    }
  };

  const handleDelete = async (doc: SourceDoc) => {
    if (!confirm(`Delete "${doc.originalName}"?`)) return;
    try {
      await sourceDocsApi.delete(doc.docId);
      await loadDocs();
    } catch (err: any) {
      setError(err.message || 'Delete failed');
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!documentType) return null;

  return (
    <div className="mt-2 mb-4 md:col-span-2">
      {/* Upload button */}
      {!disabled && (
        <div className="flex items-center gap-2 mb-2">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleUpload}
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.zip,.doc,.docx"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Upload size={14} />
            )}
            {uploading ? 'Uploading...' : 'Upload Document'}
          </button>
          <span className="text-xs text-gray-400">PDF, images, ZIP</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-sm text-red-600 mb-2">{error}</p>
      )}

      {/* Uploaded docs list */}
      {docs.length > 0 && (
        <div className="space-y-1">
          {docs.map((doc) => (
            <div
              key={doc.docId}
              className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 rounded border border-gray-100 text-sm"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileText size={14} className="text-gray-400 shrink-0" />
                <span className="truncate text-gray-700">{doc.originalName}</span>
                <span className="text-xs text-gray-400 shrink-0">{formatSize(doc.fileSize)}</span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => handleDownload(doc)}
                  className="p-1 text-blue-600 hover:text-blue-800"
                  title="Download"
                >
                  <Download size={14} />
                </button>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleDelete(doc)}
                    className="p-1 text-red-500 hover:text-red-700"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SourceDocUpload;
